import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { FaFilter, FaSearch, FaTimes } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";
import FilterSidebar from "../components/FilterSidebar";
import VerticalCard from "../components/VerticalCard";
import {
  SEARCH_PAGE_CLEAR_FILTERS,
  SEARCH_PAGE_FILTERS_TITLE,
  SEARCH_PAGE_MOBILE_FILTERS,
  SEARCH_PAGE_NO_RESULTS_SUBTITLE,
  SEARCH_PAGE_NO_RESULTS_TITLE,
  SEARCH_PAGE_RESULTS_LABEL,
} from "../constants";
import { request } from "../helpers/request";

const SearchProduct = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const searchQuery = new URLSearchParams(location.search).get("q") || "";

  const [filters, setFilters] = useState({
    category: new URLSearchParams(location.search).getAll("category"),
    brand: new URLSearchParams(location.search).getAll("brand"),
    minPrice: new URLSearchParams(location.search).get("minPrice") || "",
    maxPrice: new URLSearchParams(location.search).get("maxPrice") || "",
    minRating: new URLSearchParams(location.search).get("minRating") || "",
    sort: new URLSearchParams(location.search).get("sort") || "newest",
  });

  // Update filters state when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    setFilters({
      category: searchParams.getAll("category"),
      brand: searchParams.getAll("brand"),
      minPrice: searchParams.get("minPrice") || "",
      maxPrice: searchParams.get("maxPrice") || "",
      minRating: searchParams.get("minRating") || "",
      sort: searchParams.get("sort") || "newest",
    });
  }, [location.search]);

  const buildQueryString = (updatedFilters = filters) => {
    const params = new URLSearchParams();
    if (searchQuery) params.append("q", searchQuery);

    if (updatedFilters.category.length > 0) {
      updatedFilters.category.forEach((c) => params.append("category", c));
    }
    if (updatedFilters.brand.length > 0) {
      updatedFilters.brand.forEach((b) => params.append("brand", b));
    }
    if (updatedFilters.minPrice)
      params.append("minPrice", updatedFilters.minPrice);
    if (updatedFilters.maxPrice)
      params.append("maxPrice", updatedFilters.maxPrice);
    if (updatedFilters.minRating)
      params.append("minRating", updatedFilters.minRating);
    if (updatedFilters.sort) params.append("sort", updatedFilters.sort);

    return params.toString();
  };

  const fetchProduct = useCallback(async () => {
    setLoading(true);
    try {
      const queryString = buildQueryString();
      const response = await request({
        url: `${SummaryApi.searchProduct.url}?${queryString}`,
        method: SummaryApi.searchProduct.method,
      });
      const dataResponse = await response.json();

      if (dataResponse.success) {
        setData(dataResponse.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Search error:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, searchQuery]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const handleFilterChange = (type, value) => {
    const newFilters = { ...filters, [type]: value };
    const newQuery = buildQueryString(newFilters);
    navigate(`/search?${newQuery}`);
  };

  const clearFilters = () => {
    const params = new URLSearchParams();
    if (searchQuery) params.append("q", searchQuery);
    navigate(`/search?${params.toString()}`);
  };

  return (
    <div className="bg-surface-muted min-h-screen pt-4 md:pt-0">
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-10">
          <div>
            <div className="flex items-center gap-2 text-accent text-[10px] font-black uppercase tracking-[0.2em] mb-1">
              <FaSearch size={10} />
              <span>Search Results</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-black text-primary tracking-tight">
              {searchQuery ? `"${searchQuery}"` : "All Products"}
            </h1>
            {!loading && (
              <p className="text-xs md:text-sm text-neutral-500 font-medium mt-0.5">
                {SEARCH_PAGE_RESULTS_LABEL(searchQuery || "all", data.length)}
              </p>
            )}
          </div>

          <button
            onClick={() => setShowMobileFilters(true)}
            className="md:hidden self-start flex items-center gap-2 h-10 px-5 bg-white border border-surface-border rounded-xl font-bold text-sm shadow-sm hover:border-accent/30 transition-colors"
          >
            <FaFilter className="text-xs" /> {SEARCH_PAGE_MOBILE_FILTERS}
          </button>
        </div>

        <div className="flex gap-12 relative">
          {/* Desktop Sidebar */}
          <div className="hidden md:block w-72 sticky top-28 h-fit">
            <FilterSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
            />
          </div>

          {/* Content */}
          <div className="flex-1">
            {loading ? (
              <div className="w-full">
                <VerticalCard loading={true} />
              </div>
            ) : (
              <AnimatePresence mode="wait">
                {data.length > 0 ? (
                  <motion.div
                    key="content"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="w-full"
                  >
                    <VerticalCard data={data} loading={false} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex flex-col items-center justify-center bg-white rounded-2xl border border-surface-border shadow-card text-center p-10 md:p-16 max-w-md mx-auto mt-8"
                  >
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-surface-muted rounded-full flex items-center justify-center mb-5">
                      <FaSearch className="text-neutral-300 text-2xl md:text-3xl" />
                    </div>
                    <h2 className="text-xl md:text-2xl font-black text-primary mb-2">
                      {SEARCH_PAGE_NO_RESULTS_TITLE}
                    </h2>
                    <p className="text-sm text-neutral-500 mb-8 leading-relaxed">
                      {SEARCH_PAGE_NO_RESULTS_SUBTITLE(searchQuery)}
                    </p>
                    <button onClick={clearFilters} className="btn-primary">
                      {SEARCH_PAGE_CLEAR_FILTERS}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      <AnimatePresence>
        {showMobileFilters && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-md z-[110]"
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              className="fixed inset-y-0 right-0 h-full w-[85%] max-w-[400px] bg-white z-[120] shadow-2xl flex flex-col"
            >
              <div className="p-6 border-b border-surface-border flex items-center justify-between">
                <h3 className="text-xl font-black text-primary flex items-center gap-2">
                  <FaFilter className="text-accent" />{" "}
                  {SEARCH_PAGE_FILTERS_TITLE}
                </h3>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted"
                >
                  <FaTimes />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <FilterSidebar
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  className="shadow-none border-none"
                />
              </div>
              <div className="p-6 border-t bg-white">
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="w-full py-4 bg-primary text-white font-black rounded-2xl shadow-xl"
                >
                  Apply & Close
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchProduct;
